import cx from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { SelectTagsField } from 'components/fields/select-fields-tags/select-fields-tags';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import { Button } from 'components/ui/Button';

import { DatePickerField, InputField } from 'components/common';
import { SelectField } from 'components/fields/select-fields/select-filed';
import React, { useMemo } from 'react';
import s from './filter.module.scss';

interface Filter {
  type: string;
  field: {
    label: string;
    value: string;
  };
  options: {
    label: string;
    value: string;
  }[];
}

export const AsideFilter = ({
  isOpen,
  onSubmitForm,
  onClose,
  filters,
}: {
  filters: Filter[];
  isOpen: boolean;
  onSubmitForm: (values: any) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  const onSubmit = (values: any) => {
    onSubmitForm?.(values);
    onClose?.();
  };

  const initialValues = Object.fromEntries(filters?.map((filter) => [filter.field.value, []]));
  const validationSchema = Yup.object().shape({});

  const Form = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return () => (
      <React.Fragment>
        {filters?.map((filter) => {
          switch (filter.type) {
            case 'select':
              return (
                <SelectField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  options={filter.options}
                  isMulti={false}
                />
              );
            case 'select-multi':
              return (
                <SelectTagsField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  options={filter.options}
                  isMulti={true}
                />
              );
            case 'date':
              return (
                <DatePickerField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                />
              );
            case 'date-range':
              return (
                <DatePickerField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                  mode="range"
                />
              );
            case 'input':
              return (
                <InputField
                  label={filter.field.label}
                  name={filter.field.value}
                  key={filter.field.value}
                />
              );
            default:
              return <></>;
          }
        })}
      </React.Fragment>
    );
  }, [filters]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting, values, submitForm, setFieldValue }) => (
        <div className={cx(s['container'], { [s['open']]: isOpen })}>
          <div className={cx(s['wrapper'], { [s['open']]: isOpen })}>
            <div className={s['header']}>
              <span className={s['title']}>{t('Filter')}</span>

              <div role="button" tabIndex={0} className={s['close']} onClick={onClose}>
                <CloseIcon />
              </div>
            </div>

            <div className={s['main']}>
              <Form />
            </div>

            <div className={s['footer']}>
              <Button className={cx(s['button'], s['button-back'])} onClick={onClose}>
                {t('Back')}
              </Button>
              <Button className={cx(s['button'], s['button-save'])} onClick={submitForm}>
                {t('Save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};
